import { useState, useCallback } from "react";
import { validateField } from "../lib/validators";

// Lightweight validation layer that sits on top of a form's existing state.
// Pass a schema: { fieldName: [validators...] }.
// Errors surface only after a field is touched (blur) or after submit.
export function useFormValidation(schema = {}) {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateOne = useCallback((name, value) => {
    const err = validateField(schema[name] || [], value);
    setErrors((prev) => ({ ...prev, [name]: err }));
    return err;
  }, [schema]);

  const handleBlur = useCallback((name, value) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateOne(name, value);
  }, [validateOne]);

  // Validate every field in the schema. Returns true when the form is valid.
  const validateAll = useCallback((values) => {
    const nextErrors = {};
    const nextTouched = {};
    let ok = true;
    for (const key in schema) {
      const err = validateField(schema[key], values ? values[key] : undefined);
      nextErrors[key] = err;
      nextTouched[key] = true;
      if (err) ok = false;
    }
    setErrors(nextErrors);
    setTouched(nextTouched);
    return ok;
  }, [schema]);

  // Error string to display for a field (only once touched).
  const fieldError = useCallback(
    (name) => (touched[name] ? errors[name] || "" : ""),
    [touched, errors]
  );

  const reset = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  return { errors, touched, handleBlur, validateOne, validateAll, fieldError, reset };
}
